// frontend/src/components/DeveloperDropdown.jsx
// Developer Dropdown Component
// Purpose: Groups development tools into a single dropdown menu

import React, { useState, useRef, useEffect } from "react";
import { useTranslation } from "react-i18next";
import BackendSwitcher from "./BackendSwitcher";
import {
  isLocal,
  isPreview,
  togglePreviewMode,
} from "../config/api.js";
import { useAuth } from "../contexts/AuthContext.jsx";

const DeveloperDropdown = ({ className = "" }) => {
  const { t } = useTranslation();
  const { isAuthenticated, canAccessAdmin } = useAuth();
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  // Only show if user has admin access
  if (!isAuthenticated || !canAccessAdmin || !canAccessAdmin()) {
    return null;
  }

  return (
    <div className={`relative ${className}`} ref={dropdownRef}>
      {/* Developer Button */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center space-x-2 px-3 py-1 text-xs bg-gray-600 hover:bg-gray-700 text-white rounded transition-colors"
        aria-label="Developer tools"
      >
        <span>🔧</span>
        <span className="hidden sm:inline">Dev</span>
        <svg
          className={`w-3 h-3 text-white transition-transform ${
            isOpen ? "rotate-180" : ""
          }`}
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M19 9l-7 7-7-7"
          />
        </svg>
      </button>

      {/* Dropdown Menu */}
      {isOpen && (
        <div className="absolute right-0 mt-2 w-64 bg-white rounded-lg shadow-lg border border-gray-200 z-50">
          {/* Header */}
          <div className="p-3 border-b border-gray-200">
            <h3 className="text-sm font-semibold text-gray-900">
              Developer Tools
            </h3>
          </div>

          {/* Tools */}
          <div className="p-3 space-y-3">
            {/* Backend Switcher */}
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-2">
                Backend Environment
              </label>
              <BackendSwitcher />
            </div>

            {/* Preview Mode Toggle - Only show locally */}
            {isLocal && (
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-2">
                  Preview Mode
                </label>
                <button
                  onClick={() => {
                    togglePreviewMode();
                    setIsOpen(false);
                  }}
                  className={`w-full flex items-center justify-between px-3 py-2 text-xs rounded transition-colors ${
                    isPreview
                      ? "bg-green-100 text-green-700 border border-green-200"
                      : "bg-gray-100 text-gray-700 border border-gray-200 hover:bg-gray-200"
                  }`}
                >
                  <span>👁️ Preview Mode</span>
                  <span
                    className={`w-2 h-2 rounded-full ${
                      isPreview ? "bg-green-500" : "bg-gray-400"
                    }`}
                  ></span>
                </button>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default DeveloperDropdown;
